var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { qboui } from './styles.js';
let QboUserPreferences = class QboUserPreferences extends LitElement {
    static { this.styles = [qboui, css `
    :host {
      display: block;
      padding: 10px;
      border: 1px solid #ccc;
      background-color: #f9f9f9;
    }
    input {
      margin-bottom: 10px;
      padding: 5px;
    }
  `]; }
    constructor() {
        super();
        this.preferences = {};
        this.key = 'qbo4.ui.preferences';
        this.isEditing = false;
        this.loadPreferences();
    }
    toggleEditMode() {
        this.isEditing = !this.isEditing;
    }
    // Load preferences from localStorage
    loadPreferences() {
        const storedPreferences = localStorage.getItem(this.key);
        if (storedPreferences) {
            try {
                this.preferences = JSON.parse(storedPreferences);
            }
            catch (error) {
                console.error('Error parsing preferences from localStorage:', error);
                this.preferences = {};
            }
        }
        else {
            this.preferences = {};
        }
    }
    // Update the localStorage when preferences change
    updatePreferences() {
        localStorage.setItem('qbo4.ui.preferences', JSON.stringify(this.preferences));
    }
    // Handle input changes for preferences
    handleInputChange(event) {
        const { name, value } = event.target;
        this.preferences = { ...this.preferences, [name]: value };
    }
    // Reset preferences in localStorage
    resetPreferences() {
        localStorage.removeItem('qbo4.ui.preferences');
        this.preferences = {};
        alert('Preferences reset!');
    }
    render() {
        return html `<slot></slot>${this.isEditing ? html `
      <dialog class="qbo-card">
        <header>Manage Preferences</header>

        <!-- Date Format -->
        <label for="dateFormat">Date Format:</label>
        <input
          id="dateFormat"
          name="dateFormat"
          type="text"
          .value="${this.preferences['dateFormat'] || ''}"
          @input="${this.handleInputChange}"
        />

        <!-- Other preferences can be added similarly -->
        <footer>
            <div>
              <button @click="${this.updatePreferences}">Save</button>
              <button @click="${this.resetPreferences}">Reset</button>
            </div>
        </footer>
      </dialog>
    ` : html ``}`;
    }
};
__decorate([
    property({ type: Object }),
    __metadata("design:type", Object)
], QboUserPreferences.prototype, "preferences", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboUserPreferences.prototype, "key", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], QboUserPreferences.prototype, "isEditing", void 0);
QboUserPreferences = __decorate([
    customElement('qbo-user-preferences'),
    __metadata("design:paramtypes", [])
], QboUserPreferences);
export { QboUserPreferences };
export function setUserPreference(preference, value, storageKey = 'qbo4.ui.preferences') {
    // Get the current stored preferences
    const storedPreferences = localStorage.getItem(storageKey);
    let preferences = {};
    if (storedPreferences) {
        try {
            // Parse the stored preferences as JSON
            preferences = JSON.parse(storedPreferences);
        }
        catch (error) {
            console.error(`Error parsing ${storageKey} from localStorage:`, error, storedPreferences);
        }
    }
    // Update the specific preference
    preferences[preference] = value;
    try {
        // Save the updated preferences back to localStorage
        localStorage.setItem(storageKey, JSON.stringify(preferences));
    }
    catch (error) {
        console.error(`Error saving ${storageKey} to localStorage:`, error);
    }
}
export function getUserPreference(preference, storageKey = 'qbo4.ui.preferences') {
    const storedPreferences = localStorage.getItem(storageKey);
    if (storedPreferences) {
        try {
            // Parse the stored preferences as JSON
            const preferences = JSON.parse(storedPreferences);
            return preferences[preference];
        }
        catch (error) {
            console.error(`Error parsing ${storageKey} from localStorage:`, error, storedPreferences);
        }
    }
    return undefined;
}
export function join(values, separator = ' ') {
    return values.filter(Boolean).join(separator);
}
export function formatDate(dateString, format = null) {
    if (dateString == null || isNaN(new Date(dateString).getTime()))
        return null;
    const f = format ?? getUserPreference('dateFormat') ?? 'MM/dd/yyyy';
    // Map C# date format to Intl.DateTimeFormat options
    const options = {};
    // Handle common C# date format specifiers and map them to JS options
    if (f.includes('yyyy'))
        options.year = 'numeric';
    if (f.includes('MM'))
        options.month = f.includes('MMM') ? 'short' : '2-digit';
    if (f.includes('dd'))
        options.day = '2-digit';
    if (f.includes('HH'))
        options.hour = '2-digit'; // 24-hour f
    if (f.includes('mm'))
        options.minute = '2-digit';
    if (f.includes('ss'))
        options.second = '2-digit';
    if (f.includes('tt'))
        options.hour12 = true; // Use 12-hour f with AM/PM
    if (f.includes('dddd'))
        options.weekday = 'long'; // Full day name
    if (f.includes('ddd'))
        options.weekday = 'short'; // Abbreviated day name
    // Format the date using Intl.DateTimeFormat with the parsed options
    const formattedDate = new Intl.DateTimeFormat(undefined, options).format(new Date(dateString));
    return formattedDate;
}
export function formatMoney(amount, currency = 'USD', locale = 'en-US') {
    if (!amount)
        return '';
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
    }).format(Number(amount));
}
export function formatPercentage(value, decimals = 2, locale = 'en-US') {
    if (!value)
        return '';
    return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(Number(value));
}
export function timeRemaining(targetDate, interval = 'months') {
    if (targetDate == null || isNaN(new Date(targetDate).getTime()))
        return null;
    let years = new Date(targetDate).getFullYear() - new Date().getFullYear();
    let months = new Date(targetDate).getMonth() - new Date().getMonth();
    if (months < 0) {
        years -= 1;
        months = 12 + months;
    }
    if (years < 1 && months < 1)
        return html ``;
    else if (years < 1 && months > 0)
        return html `${months} month${months > 1 ? 's' : ''}`;
    else
        return html `${years} year${years > 1 ? 's' : ''}, ${months} month${months != 1 ? 's' : ''} (${years * 12 + months})`;
}
